// Contracts — javelin v4 packs (build / list / download).
// Worker side: apps/api/src/javelin/packs.ts. Web side: jav() calls in
// JavPackForm_v4 / JavPackHomeDetail (apps/web/src/javelin).
import { z } from 'zod';

// POST /api/javelin/packBuild. camera_refresh / referral mirror the
// builder's PackInputs (v4_pack_builder.ts) — the VALUES are still
// builder-validated against canon (NonCanonicalError → 400
// validation_failed with field detail); the contract pins the
// container shapes. String ids are .trim()-normalized in the contract
// (visible normalization decision, replaces the handlers' String().trim()).
export const JavPackBuildReqSchema = z.object({
  participant_id: z.string().trim().min(1),
  // Required by the handler when the stored session is split — that
  // check stays domain-side (it depends on the participant row).
  main_session_id: z.string().trim().optional(),
  camera_refresh: z.object({
    contrast: z.array(z.string().nullable()).optional(),
    lux: z.array(z.number().nullable()).optional(),
    clutter: z.array(z.string().nullable()).optional(),
    hp: z.array(z.string().nullable()).optional(),
    mirror: z.array(z.string().nullable()).optional(),
  }).optional(),
  picked_codes: z.array(z.string()).optional(),
  referral: z.object({
    name: z.string().optional(),
    phone: z.string().optional(),
  }).optional(),
});
export type JavPackBuildReq = z.infer<typeof JavPackBuildReqSchema>;

export const JavPackBuildResSchema = z.object({
  ok: z.literal(true),
  participant_id: z.string(),
  r2_key: z.string(),
  download_url: z.string(),
  keys_count: z.number(),
});
export type JavPackBuildRes = z.infer<typeof JavPackBuildResSchema>;

// GET /api/javelin/packsList?home_id=
export const JavPacksListQuerySchema = z.object({
  home_id: z.string().trim().min(1),
});
export type JavPacksListQuery = z.infer<typeof JavPacksListQuerySchema>;

export const JavPackListItemSchema = z.object({
  participant_id: z.string(),
  r2_key: z.string(),
  packed_at: z.string(),
  // NOT NULL DEFAULT '' (0018) — '' means "not delivered yet".
  tg_delivered_at: z.string(),
});
export type JavPackListItem = z.infer<typeof JavPackListItemSchema>;

export const JavPacksListResSchema = z.object({
  home_id: z.string(),
  packs: z.array(JavPackListItemSchema),
});
export type JavPacksListRes = z.infer<typeof JavPacksListResSchema>;

// GET /api/javelin/packDownload?participant_id= — response is a binary
// attachment stream (the pack JSON file), so there is no Res schema;
// the contract is the query shape only.
export const JavPackDownloadQuerySchema = z.object({
  participant_id: z.string().trim().min(1),
});
export type JavPackDownloadQuery = z.infer<typeof JavPackDownloadQuerySchema>;

// ── Журнал отказов сборки пака (migration 0045) ───────────────────────
// Отказ билдера живёт только в HTTP-ответе, а инструмент модератора
// показывает его как «validation_failed — [object Object]». Журнал делает
// отказ видимым С НАШЕЙ стороны: и по конкретному человеку, и по всем
// сразу — поле, которое «болит» у многих, означает дыру в форме.
export const JavPackErrorsQuerySchema = z.object({
  mod_id: z.string().default(''),
  participant_id: z.string().default(''),
  /** За сколько последних дней смотреть. */
  days: z.coerce.number().int().min(1).max(90).default(7),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export const JavPackErrorsResSchema = z.object({
  /** Отказы по одному, свежие сверху. */
  errors: z.array(z.object({
    ts: z.string(),
    participant_id: z.string(),
    mod_id: z.string().default(''),
    source: z.string().default(''),
    field: z.string().default(''),
    value: z.string().default(''),
    reason: z.string().default(''),
  })).default([]),
  /** Свод: какое поле сколько раз и у скольких участников. */
  by_field: z.array(z.object({
    field: z.string(),
    n: z.number(),
    participants: z.number(),
    mods: z.string().default(''),
  })).default([]),
});
export type JavPackErrorsRes = z.infer<typeof JavPackErrorsResSchema>;
